import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { sanitizeClinicalHtml } from '../../utils/clinicalHtml';
import { formatEvolutionAdditionDate, type EvolutionAddition } from '../../utils/evolutionAdditionPolicy';
import {
  evolutionAdditionHeadingSx,
  evolutionAdditionItemSx,
  evolutionAdditionPanelSx,
} from './evolutionAdditionsStyles';
import { EvolutionAdditionAttachmentsReadonly } from './EvolutionAdditionFileAttachments';

type Props = {
  evolutionId: string;
  /** Quando false, não busca (ex.: evolução colapsada). */
  active?: boolean;
  /** Se o resumo do painel já trouxe os itens, evita request extra. */
  initialItems?: EvolutionAddition[] | null;
  compact?: boolean;
};

export default function EvolutionAdditionsDisplay({
  evolutionId,
  active = true,
  initialItems,
  compact = false,
}: Props) {
  const [items, setItems] = useState<EvolutionAddition[]>(() => initialItems ?? []);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(() => Array.isArray(initialItems));

  const load = useCallback(async () => {
    if (!evolutionId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/evolutions/${evolutionId}/additions`));
      setItems(data?.data || []);
      setLoaded(true);
    } catch {
      setItems([]);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [evolutionId]);

  useEffect(() => {
    if (Array.isArray(initialItems)) {
      setItems(initialItems);
      setLoaded(true);
    }
  }, [initialItems]);

  useEffect(() => {
    if (!active || loaded || !evolutionId) return;
    void load();
  }, [active, loaded, evolutionId, load]);

  if (!active && !items.length) return null;

  if (loading && !items.length) {
    return (
      <Box sx={evolutionAdditionPanelSx}>
        <CircularProgress size={18} sx={{ color: '#3b82f6' }} />
      </Box>
    );
  }

  if (!items.length) return null;

  return (
    <Box sx={evolutionAdditionPanelSx}>
      <Typography variant="caption" sx={evolutionAdditionHeadingSx}>
        Informações adicionadas
      </Typography>
      <Stack spacing={compact ? 1 : 1.25}>
        {items.map((row) => (
          <Box key={row.id} sx={evolutionAdditionItemSx}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 0.5 }}
            >
              {formatEvolutionAdditionDate(row.added_at)}
              {row.professional?.full_name ? ` · ${row.professional.full_name}` : ''}
            </Typography>
            <Box
              className="clinical-html"
              sx={{
                fontSize: compact ? '0.75rem' : 14,
                lineHeight: 1.55,
                color: '#475569',
                '& p': { margin: '0 0 0.5rem' },
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeClinicalHtml(row.body_html || '') }}
            />
            <EvolutionAdditionAttachmentsReadonly
              documents={row.documents}
              images={row.images}
              compact={compact}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
